import { useState, useCallback, useRef } from 'react';

import { useUIStore } from '@/store';

/**
 * Hook for copying text to clipboard with auto-resetting `copied` state.
 * Shows a toast notification on success.
 */
export function useCopyToClipboard(resetDelay = 2000): {
  copy: (text: string) => Promise<void>;
  copied: boolean;
} {
  const [copied, setCopied] = useState(false);
  const timerRef = useRef<ReturnType<typeof setTimeout> | null>(null);

  const copy = useCallback(
    async (text: string) => {
      try {
        if (navigator.clipboard?.writeText) {
          await navigator.clipboard.writeText(text);
        } else {
          // Fallback for older browsers / insecure contexts
          const textarea = document.createElement('textarea');
          textarea.value = text;
          textarea.style.position = 'fixed';
          textarea.style.opacity = '0';
          document.body.appendChild(textarea);
          textarea.select();
          document.execCommand('copy');
          document.body.removeChild(textarea);
        }

        setCopied(true);
        useUIStore.getState().showSuccess('Copied to clipboard');

        if (timerRef.current) clearTimeout(timerRef.current);
        timerRef.current = setTimeout(() => setCopied(false), resetDelay);
      } catch {
        useUIStore.getState().showError('Failed to copy');
      }
    },
    [resetDelay]
  );

  return { copy, copied };
}
